import React from "react";
import {
  Box,
  Button,
  Card,
  Link,
  CardContent,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Dialog,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { gql, useLazyQuery } from "@apollo/client";
import { enqueueSnackbar } from "notistack";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsConditions from "./Terms&Conditions";
import DrawerItems from "../../Drawer/DrawerItem";
import { featureMap } from "../../Drawer/userDrawerItems";


const LOGIN_QUERY = gql(`
query Query($username: String!, $password: String!) {
  login(username: $username, password: $password) {
    user {
      user_id
      username
      assigned_role
    }
    token
    features {
      feature_id
      feature_name
    }
  }
}
`);

function LoginForm() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const redirectURL = searchParams.get("redirect");

  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [isDone, setIsDone] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [loginUser] = useLazyQuery(LOGIN_QUERY);
  const [openPrivacyDialog, setOpenPrivacyDialog] = React.useState(false);
  const [openTermsDialog, setOpenTermsDialog] = React.useState(false);
  const [termsAccepted, setTermsAccepted] = React.useState(false);


  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    const resp = await loginUser({
      variables: { username, password },
    });
    if (resp.error) {
      enqueueSnackbar("Invalid Username or Password!", { variant: "error" });
      setIsLoading(false);
      return;
    }
    if (resp.data && resp.data.login && resp.data.login.token) {
      const token = resp.data.login.token;
      const tokenParts = token.split(".");
      if (tokenParts.length !== 3) {
        enqueueSnackbar("Invalid Token Format!", { variant: "error" });
        setIsLoading(false);
        return;
      }
      const login = auth.logIn(token);
      if (!login) {
        enqueueSnackbar("Invalid Token!", { variant: "error" });
        setIsLoading(false);
        return;
      }
      enqueueSnackbar("Success! Logged In", {
        variant: "success",
        autoHideDuration: 2000,
      });
      setIsLoading(false);
      setIsDone(true);

      const features = resp.data.login.features || [];
      const featureNames = features.map((f: any) => f.feature_name);

      const firstFeatureItem = DrawerItems.find((item) => {
        const requiredFeatures = featureMap[item.key];
        // If not in map, assume it's protected and don't redirect to it by default
        if (requiredFeatures === undefined) return false;
        // If mapped to an empty array (length 0), it's public (like Account)
        if (requiredFeatures.length === 0) return true;
        // Return true if user has ANY of the required features
        return requiredFeatures.some((feature) => featureNames.includes(feature));
      });

      const targetPath = redirectURL || (firstFeatureItem ? firstFeatureItem.path : "/device-manager");

      setTimeout(() => {
        navigate(targetPath);
      }, 1000);
      return;
    }
    enqueueSnackbar("Unable to Login!", { variant: "error" });
    setIsLoading(false);
  };

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length <= 32) {
      setUsername(e.target.value);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length <= 32) {
      setPassword(e.target.value);
    }
  };
  const handleOpenPrivacyDialog = () => {
    setOpenPrivacyDialog(true);
  };
  const handleClosePrivacyDialog = () => {
    setOpenPrivacyDialog(false);
  };
  const handleOpenTermsDialog = () => {
    setOpenTermsDialog(true);
  };

  const handleCloseTermsDialog = () => {
    setOpenTermsDialog(false);
  };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sx={{ height: "100vh", backgroundColor: "#ffffffff" }}
    >
      <CardContent>
        <Box textAlign="right" mr={10} mb={3}>
          <Typography variant="h5" fontWeight="bold">
            Welcome to OEM Tool
          </Typography>
          <Typography variant="body1" color="textSecondary" mt={1} mr={5}>
            Please enter the details
          </Typography>
        </Box>
        <form onSubmit={handleFormSubmit}>
          <Box ml={4} mb={2}>
            <TextField
              fullWidth
              placeholder="Username"
              variant="outlined"
              value={username}
              onChange={handleUsernameChange}
              inputProps={{ maxLength: 32 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircleIcon />
                  </InputAdornment>
                ),
              }}
              required
            />
          </Box>
          <Box ml={4} mb={3}>
            <TextField
              fullWidth
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              variant="outlined"
              value={password}
              onChange={handlePasswordChange}
              inputProps={{ maxLength: 32 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleShowPassword}
                      onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()}
                      edge="end"
                      tabIndex={-1}
                      sx={{ minWidth: 0, padding: 1, color: '#888' }}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              required
            />
          </Box>

          {/* Terms and Conditions Checkbox - Single line above button */}
          <Box ml={4} mb={2} display="flex" alignItems="center">
            <Checkbox
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              color="primary"
              required
            />
            <Typography variant="body2">
              I accept your Terms and Conditions
            </Typography>
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading || !termsAccepted}
            sx={{
              ml: 4, mb: 3,
              backgroundColor: "#e60012",
              color: "#fff",
              fontWeight: "bold",
              borderRadius: "8px",
              height: "50px",
              width: "400px",
              padding: "10px 0",
              "&:hover": {
                backgroundColor: "#cc0010"
              },
              "&:disabled": {
                backgroundColor: "#f5f5f5",
                color: "#9e9e9e"
              },
              position: "relative"
            }}
          >
            {isLoading ? "Logging in..." : "LOGIN"}
          </Button>
        </form>

        {/* Privacy Policy and Terms links in same line below button */}
        <Box ml={4} display="flex" justifyContent="center" gap={4}>
          <Link
            component="button"
            sx={{ cursor: 'pointer' }}
            onClick={handleOpenPrivacyDialog}
            underline="hover"
          >
            Privacy Policy
          </Link>
          <Link
            component="button"
            sx={{ cursor: 'pointer' }}
            onClick={handleOpenTermsDialog}
            underline="hover"
          >
            Terms and Conditions
          </Link>
        </Box>

        {/* Privacy Policy Dialog */}
        <Dialog
          open={openPrivacyDialog}
          onClose={handleClosePrivacyDialog}
          maxWidth="md"
          fullWidth
        >
          <PrivacyPolicy close={handleClosePrivacyDialog} />
        </Dialog>

        {/* Terms and Conditions Dialog */}
        <Dialog
          open={openTermsDialog}
          onClose={handleCloseTermsDialog}
          maxWidth="md"
          fullWidth
        >
          <TermsConditions close={handleCloseTermsDialog} />
        </Dialog>
      </CardContent>
    </Grid>
  );
}

export default LoginForm;